import { Box, Text } from '@chakra-ui/react';
import { Input, InputVariant } from '@pluxee-design-system/core';
import withFastField, { WithFastFieldProps } from 'common/forms/withFastField';
import { ChangeEventHandler, memo, useCallback } from 'react';
import type { InputControlProps } from './InputControl';

interface AccountNumberControlProps extends InputControlProps {
  format?: string;
}

export const formatValue = (rawValue: string, format?: string) => {
  if (rawValue === '') return '';

  let value = rawValue.replace(/\s/g, '');

  if (format && value.startsWith(format)) {
    value = value.substring(format.length);
  }

  return value;
};

const AccountNumberControl = ({
  disabled,
  format,
  field,
  field: { setValue },
  name,
  readonly,
  skipSuccess,
  skipVariant,
  ...inputProps
}: AccountNumberControlProps & WithFastFieldProps<string>) => {
  const hasError = Boolean(field.error);
  // handle any format to proper format
  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const value = formatValue(event.target.value, format);
      setValue(value);
    },
    [format, setValue],
  );

  return (
    <Box flex="2 0 auto">
      <Input
        type="text"
        name={field.name}
        onBlur={inputProps.onBlur ?? field.onBlur}
        onChange={handleChange}
        value={field.value}
        {...inputProps}
        disabled={inputProps.isReadOnly || readonly ? undefined : disabled}
        variant={
          skipVariant
            ? field.isTouched && hasError
              ? InputVariant.ERROR
              : undefined
            : inputProps.isReadOnly || readonly
              ? InputVariant.READONLY
              : field.isTouched && hasError
                ? InputVariant.ERROR
                : field.isTouched && !skipSuccess
                  ? InputVariant.SUCCESS
                  : InputVariant.FILLED
        }
        helpText={field.isTouched && hasError ? field.error : inputProps.helpText}
        leftElement={
          format && (
            <Text variant="body.largeDemibold" color="semantic.text.primary">
              {format}
            </Text>
          )
        }
      />
    </Box>
  );
};

const MemoizedAccountNumberControl = memo(AccountNumberControl);

export default withFastField(MemoizedAccountNumberControl);
